/**
 * Packages API + TanStack Query hooks.
 *
 * Backend endpoints:
 *   GET    /v1/catalog/packages
 *   GET    /v1/catalog/packages/{id}
 *   POST   /v1/catalog/packages
 *   PATCH  /v1/catalog/packages/{id}
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';
import type { EntityListResult } from '@/components/entity/types';

export type PackageAddOn = { name: string; price: number };
export type PackageType = 'one_time' | 'subscription';
export type BillingCycle = 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
export type ValidityType = 'by_date' | 'by_range';

// Augment the generated type with the 11E.4 Classcard-parity fields not yet
// regenerated into shared/api-types/index.ts (same pattern as
// StaffResponse.bio in people/api/staff.ts).
type _GeneratedPackageResponse = components['schemas']['PackageResponse'];
export type PackageResponse = _GeneratedPackageResponse & {
  package_type: PackageType;
  billing_cycle: BillingCycle | null;
  sessions_unlimited: boolean;
  validity_type: ValidityType;
  validity_end_date: string | null;
  audience_label: string;
  name_override: string | null;
  display_image_url: string | null;
  makeup_sessions_allowed: boolean;
  enrollment_limit_days_per_week: number | null;
  pro_rata_enabled: boolean;
  published_online: boolean;
  registration_fee: number;
  add_ons: PackageAddOn[];
  reschedule_limit: number;
  // 11F.1 — server-resolved display names for subject_ids, same order.
  // Canonical human-readable source; subject_ids stays internal-reference-only.
  subject_names: string[];
};

export type PackageCreate = Omit<
  PackageResponse,
  'id' | 'created_at' | 'updated_at' | 'classcard_id' | 'subject_names'
>;
export type PackageUpdate = Partial<PackageCreate>;
// items uses the augmented PackageResponse (not the raw generated one) —
// same reasoning as PackageResponse itself above.
export type PaginatedPackages = Omit<
  components['schemas']['PaginatedResult_PackageResponse_'],
  'items'
> & { items: PackageResponse[] };

export interface ListPackagesParams {
  status?: string;
  subject_id?: string;
}

export interface ListPackagesPagedParams {
  status?: string;
  subject_id?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export const packageKeys = {
  all: ['catalog', 'packages'] as const,
  list: (params: ListPackagesParams) => ['catalog', 'packages', 'list', params] as const,
  pagedList: (params: ListPackagesPagedParams) => ['catalog', 'packages', 'paged', params] as const,
  detail: (id: string) => ['catalog', 'packages', 'detail', id] as const,
};

export async function listPackages(params: ListPackagesParams = {}): Promise<PaginatedPackages> {
  return api.get<PaginatedPackages>('/v1/catalog/packages', {
    query: {
      status: params.status || undefined,
      subject_id: params.subject_id || undefined,
    },
  });
}

export async function listPackagesPaged(
  params: ListPackagesPagedParams,
): Promise<EntityListResult<PackageResponse>> {
  return api.get<EntityListResult<PackageResponse>>('/v1/catalog/packages', {
    query: {
      status: params.status || undefined,
      subject_id: params.subject_id || undefined,
      search: params.search || undefined,
      sort: params.sort || undefined,
      order: params.order || undefined,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  });
}

export async function getPackage(id: string): Promise<PackageResponse> {
  return api.get<PackageResponse>(`/v1/catalog/packages/${id}`);
}

export async function createPackage(body: PackageCreate): Promise<PackageResponse> {
  return api.post<PackageResponse>('/v1/catalog/packages', body);
}

export async function updatePackage(id: string, body: PackageUpdate): Promise<PackageResponse> {
  return api.patch<PackageResponse>(`/v1/catalog/packages/${id}`, body);
}

export function usePackages(params: ListPackagesParams = {}) {
  return useQuery({
    queryKey: packageKeys.list(params),
    queryFn: () => listPackages(params),
  });
}

export function usePackagesList(params: ListPackagesPagedParams) {
  return useQuery({
    queryKey: packageKeys.pagedList(params),
    queryFn: () => listPackagesPaged(params),
    placeholderData: (prev) => prev,
  });
}

/** staleTime:0 — always re-fetch to fix edit pre-load stale-cache bug. */
export function usePackage(id: string) {
  return useQuery({
    queryKey: packageKeys.detail(id),
    queryFn: () => getPackage(id),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useCreatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPackage,
    onSuccess: () => qc.invalidateQueries({ queryKey: packageKeys.all }),
  });
}

export function useUpdatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PackageUpdate }) => updatePackage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: packageKeys.all }),
  });
}



